import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Search, Star, ShoppingCart, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { useCart } from '../hooks/useCart';
import { FaWhatsapp } from 'react-icons/fa';
import { formatPrice } from '../utils/helpers';
import { productService } from '../services/productService';
import type { FirebaseProduct } from '../types/product';

interface BrandGroup {
  brand: string;
  productCount: number;
  products: FirebaseProduct[];
  averageRating: number;
  categories: string[];
  description?: string;
}

const BRANDS_PER_PAGE = 5;
const PRODUCTS_PER_BRAND = 8;
const BrandsPage: React.FC = () => {
  const { addToCart } = useCart();
  const [products, setProducts] = useState<FirebaseProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const handleWhatsAppClick = (productName: string) => {
    const url = `https://wa.me/254706788802?text=Hi, I'm interested in ${encodeURIComponent(productName)}`;
    window.open(url, '_blank');
  };

  // Fetch all products on mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const productsData = await productService.getProducts();
        setProducts(productsData);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError('Failed to load products. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Group products by brand
  const brandGroups = useMemo(() => {
    const groups = products.reduce((acc: { [key: string]: BrandGroup }, product) => {
      if (!acc[product.brand]) {
        acc[product.brand] = {
          brand: product.brand,
          productCount: 0,
          products: [],
          averageRating: 0,
          categories: [],
          description: `Quality ${product.brand} products for all your needs`
        };
      }
      
      acc[product.brand].products.push(product);
      acc[product.brand].productCount += 1;
      
      // Calculate average rating
      const totalRating = acc[product.brand].products.reduce((sum, p) => sum + (p.rating || 0), 0);
      acc[product.brand].averageRating = totalRating / acc[product.brand].productCount;
      
      // Add unique categories
      if (!acc[product.brand].categories.includes(product.category)) {
        acc[product.brand].categories.push(product.category);
      }

      return acc;
    }, {});

    return Object.values(groups).sort((a, b) => b.averageRating - a.averageRating);
  }, [products]);

  // Filter brands based on search
  const filteredBrands = useMemo(() => 
    brandGroups.filter(group =>
      group.brand.toLowerCase().includes(searchQuery.toLowerCase()) ||
      group.categories.some(cat => cat.toLowerCase().includes(searchQuery.toLowerCase())) ||
      group.products.some(product => 
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
    ), [brandGroups, searchQuery]
  );

  // Calculate pagination
  const totalPages = Math.ceil(filteredBrands.length / BRANDS_PER_PAGE);
  const startIndex = (currentPage - 1) * BRANDS_PER_PAGE;
  const paginatedBrands = filteredBrands.slice(startIndex, startIndex + BRANDS_PER_PAGE);

  const handleAddToCart = async (product: FirebaseProduct) => {
    try {
      // Verify current stock before adding to cart
      const updatedProduct = await productService.getProduct(product.id);
      if (!updatedProduct || updatedProduct.stock === 0) {
        setError('Product is out of stock');
        return;
      }

      addToCart(product);
      setShowSuccessToast(true);
      setTimeout(() => setShowSuccessToast(false), 3000);
    } catch (err) {
      setError('Failed to add product to cart');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="space-y-12">
          {[1, 2, 3].map((index) => (
            <div key={index} className="bg-white rounded-lg shadow-sm overflow-hidden animate-pulse">
              {/* Brand Header Skeleton */}
              <div className="border-b p-6">
                <div className="h-8 bg-gray-200 rounded w-1/4 mb-4" />
                <div className="flex items-center space-x-4 mb-4">
                  <div className="h-4 bg-gray-200 rounded w-16" />
                  <div className="h-4 bg-gray-200 rounded w-24" />
                </div>
                <div className="h-4 bg-gray-200 rounded w-3/4" />
              </div>

              {/* Products Grid Skeleton */}
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {[1, 2, 3, 4].map((productIndex) => (
                    <div key={productIndex} className="bg-gray-50 rounded-lg p-4">
                      <div className="aspect-square bg-gray-200 rounded-lg mb-4" />
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                      <div className="h-4 bg-gray-200 rounded w-1/2" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 text-red-600 p-6 rounded-lg text-center">
          <p className="text-lg mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="text-brand-blue-500 hover:text-brand-blue-600 font-medium"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Premium Brands | Perfect Paper</title>
        <meta 
          name="description" 
          content="Explore our collection of premium paper brands and stationery supplies. Quality products from leading manufacturers worldwide."
        />
      </Helmet>

      {/* Success Toast */}
      {showSuccessToast && (
        <div className="fixed top-4 right-4 bg-green-500 text-black px-4 py-3 rounded-lg 
          shadow-lg flex items-center gap-2 animate-fade-in z-50">
          <Check className="h-5 w-5" />
          Added to cart successfully
        </div>
      )}

      {/* Hero Section - Updated */}
      <div className="relative bg-brand-blue-500 text-white">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-blue-700/80 to-brand-blue-500/90"></div>
        <div className="relative container mx-auto px-4 py-20">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Our Premium Brands</h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              Discover quality paper products from world-renowned manufacturers, trusted by 
              businesses and professionals nationwide.
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Rest of your content... */}
        {/* Search */}
        <div className="max-w-xl mx-auto mb-12">
          <div className="relative">
            <input
              type="text"
              placeholder="Search brands, products, or categories..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1); // Reset to first page on search
              }}
              className="w-full px-4 py-3 pl-12 bg-white border rounded-lg shadow-sm
                focus:outline-none focus:ring-2 focus:ring-brand-blue-500/20 
                focus:border-brand-blue-500"
            />
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
          </div>
        </div>

        {/* Brands Grid */}
        <div className="space-y-12">
          {paginatedBrands.map((brandGroup) => (
            <div 
              key={brandGroup.brand} 
              className="bg-white rounded-lg shadow-sm overflow-hidden"
            >
              {/* Brand Header */}
              <div className="border-b p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      {brandGroup.brand}
                    </h2>
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center">
                        <Star className="h-5 w-5 text-yellow-400 fill-current" />
                        <span className="ml-1 text-gray-600">
                          {brandGroup.averageRating.toFixed(1)}
                        </span>
                      </div>
                      <span className="text-gray-600">
                        {brandGroup.productCount} products
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {brandGroup.categories.map((category) => (
                      <span
                        key={category}
                        className="px-3 py-1 text-sm bg-gray-100 text-gray-600 rounded-full"
                      >
                        {category}
                      </span>
                    ))}
                  </div>
                </div>
                <p className="text-gray-600">{brandGroup.description}</p>
              </div>

              {/* Products Grid */}
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {brandGroup.products.slice(0, PRODUCTS_PER_BRAND).map((product) => (
                    <div
                      key={product.id}
                      className="bg-gray-50 rounded-lg p-4 hover:shadow-md transition-all duration-300"
                    >
                      <Link to={`/product/${product.slug}`}>
                        <div className="aspect-square bg-white rounded-lg mb-4 overflow-hidden">
                          <img
                            src={product.images[0]?.url || '/api/placeholder/400/400'}
                            alt={product.name}
                            className="w-full h-full object-contain p-2 hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <h3 className="font-medium text-gray-900 hover:text-brand-blue-500 
                          transition-colors mb-2 line-clamp-2">
                          {product.name}
                        </h3>
                      </Link>
                      
                      <div className="flex items-center space-x-2 mb-3">
                        <Star className="h-4 w-4 text-yellow-400 fill-current" />
                        <span className="text-sm text-gray-600">
                          {product.rating} ({product.reviewCount || 0})
                        </span>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="font-bold text-gray-900">
                          {formatPrice(product.price)}
                        </span>
                        <button
                          onClick={() => handleWhatsAppClick(product.name)}
                          className="p-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
                        >
                          <FaWhatsapp className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleAddToCart(product)}
                          disabled={product.stock === 0}
                          className={`p-2 rounded-lg transition-colors ${
                            product.stock === 0
                              ? 'bg-gray-200 cursor-not-allowed'
                              : 'bg-brand-blue-500 hover:bg-brand-blue-600 text-white'
                          }`}
                        >
                          <ShoppingCart className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {brandGroup.products.length > PRODUCTS_PER_BRAND && (
                  <div className="mt-6 text-center">
                    <Link
                      to={`/products?brand=${encodeURIComponent(brandGroup.brand)}`}
                      className="inline-flex items-center text-brand-blue-500 
                        hover:text-brand-blue-600 font-medium"
                    >
                      View all {brandGroup.products.length} products
                    </Link>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {filteredBrands.length > BRANDS_PER_PAGE && (
          <div className="mt-12 flex justify-center">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border text-gray-600 hover:text-brand-blue-500 
                  disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    currentPage === page
                      ? 'bg-brand-blue-500 text-white'
                      : 'text-gray-600 hover:text-brand-blue-500'
                  }`}
                >
                  {page}
                </button>
              ))}
              
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg border text-gray-600 hover:text-brand-blue-500 
                  disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        {/* No Results */}
        {filteredBrands.length === 0 && !loading && (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 
              bg-gray-100 rounded-full mb-4">
              <Search className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No brands found
            </h3>
            <p className="text-gray-600 mb-4">
              Try adjusting your search terms
            </p>
            <button
              onClick={() => setSearchQuery('')}
              className="text-brand-blue-500 hover:text-brand-blue-600 font-medium"
            >
              Clear search
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default BrandsPage;